import * as Effects from "redux-saga/effects";
import * as GrainTypeAction from "../actions/grainTypeAction";
import * as GrainProfileAction from "../actions/grainProfileAction";
import * as AppAction from "../actions/appAction";
import * as JobAction from "../actions/jobAction";
import * as NotificationAction from "../actions/notificationAction";
import * as BluetoothAction from "../actions/bluetoothAction";
import * as ReportAction from "../actions/reportAction";
import * as UploadAction from "../actions/uploadAction";
import * as UserAction from "../actions/userAction";
import * as OrgAction from "../actions/orgAction";
import * as userGuideActions from "../actions/userGuideActions";
import * as LoginAction from "../actions/loginAction";

import { updateLocale } from "../actions/appAction";
import { call, takeLatest, select, take } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { setLocale, setInitialLocale } from '../../utils/i18n';
import { IAppActionState } from '../reducers/appReducer';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import { deriveIsOnlineFromNetInfoState } from '../../utils/network/deriveIsOnlineFromNetInfoState';
import { isElectron } from '../../utils/miscUtils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { EXPLICIT_LOGOUT_STORAGE_KEY } from '../../constants';

const put: any = Effects.put;
const takeEvery: any = Effects.takeEvery;
const all: any = Effects.all;
const fork: any = Effects.fork;

const getCurrentLocale = (state: { app: IAppActionState }) => state.app.locale;

function* handleLocaleChange(action: ReturnType<typeof updateLocale>) {
  try {
    const { payload: newLocale } = action;
    yield call(setLocale, newLocale);
  } catch (error) {
    console.error('Failed to change locale:', error);
  }
}

function* initializeLocale() {
  try {
    const initialLocale: string = yield call(setInitialLocale);
    console.log('initialLocale', initialLocale);

    const currentLocale: string = yield select(getCurrentLocale);
    console.log('currentLocale', currentLocale);

    if (initialLocale !== currentLocale) {
      console.log('Dispatching updateLocale action');
      yield put(updateLocale(initialLocale));
    }
  } catch (error) {
    console.error('Failed to initialize locale:', error);
  }
}

function createNetworkStatusChannel() {
  return eventChannel((emit) => {
    if (isElectron()) {
      const getOnline = () => {
        try {
          if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
            return navigator.onLine;
          }
        } catch {
          // ignore
        }
        return true;
      };

      emit(getOnline());

      // Use browser online/offline events on desktop builds.
      if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return () => {};
      }

      const onOnline = () => emit(true);
      const onOffline = () => emit(false);
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);

      // Poll occasionally to catch missed events (sleep/wake, etc).
      const interval = setInterval(() => emit(getOnline()), 30 * 1000);

      return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
        clearInterval(interval);
      };
    }

    NetInfo.fetch().then((state: NetInfoState) => {
      const isOnline = deriveIsOnlineFromNetInfoState(state);
      emit(isOnline);
    }).catch(() => {
      emit(true);
    });

    const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
      const isOnline = deriveIsOnlineFromNetInfoState(state);
      emit(isOnline);
    });

    return () => unsubscribe();
  });
}

function* watchNetworkStatus() {
  const channel = yield call(createNetworkStatusChannel);
  try {
    while (true) {
      const isOnline = yield take(channel);
      yield put(AppAction.setNetworkStatus(isOnline));
    }
  } finally {
    channel.close();
  }
}


export function* resetFilters(_action: ReturnType<typeof AppAction.resetFilters>): any {
  try {
    yield put(AppAction.setGrainIdFilter('all'));
    yield put(AppAction.setDateRange(undefined));
    yield put(AppAction.setSearchQuery(undefined));
    yield put(AppAction.setCenterIdFilter('all'));
    yield put(AppAction.setDistrictFilter('all'));
    yield put(AppAction.setStateFilter('all'));
  } catch (error: any) {
  }
}

export function* logout(action: ReturnType<typeof AppAction.logout>): any {
  try {
    yield call([AsyncStorage, 'setItem'], EXPLICIT_LOGOUT_STORAGE_KEY, '1');
    yield put(GrainTypeAction.reset());
    yield put(GrainProfileAction.reset());
    yield put(OrgAction.reset());
    yield put(JobAction.reset());
    yield put(NotificationAction.reset());
    yield put(BluetoothAction.reset());
    yield put(ReportAction.reset());
    yield put(UploadAction.reset());
    yield put(UserAction.reset());
    yield put(userGuideActions.resetUserGuide());
    yield put(AppAction.reset());
    yield put(LoginAction.setUserLoggedIn("NOT_LOGGED_IN"));
  } catch (error: any) {
    
  }
}

export function* forceLogout(action: ReturnType<typeof AppAction.forceLogout>): any {
  try {
  } catch (error: any) {

  }
}

/*
 * WATCHERS
 */
function* watchLocaleChange() {
  yield takeLatest(updateLocale, handleLocaleChange);
}

export function* watchResetFilters() {
  yield takeEvery(AppAction.resetFilters, resetFilters);
}

export function* watchLogout() {
  yield takeEvery(AppAction.logout, logout);
}

export function* watchForceLogout() {
  yield takeEvery(AppAction.forceLogout, forceLogout);
}

export default function* root() {
  yield all([
    fork(watchResetFilters),
    fork(watchLogout),
    fork(watchForceLogout),
    fork(initializeLocale),
    fork(watchLocaleChange),
    fork(watchNetworkStatus)
  ]);
}
